/**
 * Ticket 04: PDU Socket Grid & Interactive Outlet Matrix Pure Logic
 * Aggregates PDU power ports, cable connections, and rack equipment
 * to build an interactive matrix of 24 outlets with consumer metadata.
 */

export type SocketConnectorType = 'C13' | 'C19' | 'other';
export type SocketStatus = 'free' | 'occupied' | 'reserved';

export interface PduSocketItem {
  portId: string;
  outletIndex: number;
  name: string;
  connectorType: SocketConnectorType;
  status: SocketStatus;
  cableId?: string;
  connectedDeviceId?: string;
  connectedDeviceName?: string;
  connectedDeviceU?: number;
  connectedDeviceMount?: string;
  connectedPortName?: string;
  powerDrawW?: number;
}

export interface PduOutletMatrixData {
  pduId: string;
  pduName: string;
  feedLetter: 'A' | 'B';
  phase: string;
  ratedCurrentA: number;
  totalOutlets: number;
  freeOutlets: number;
  occupiedOutlets: number;
  sockets: PduSocketItem[];
}

export interface BuildPduOutletMatrixParams {
  pdu: any;
  pduPorts?: any[];
  cables?: any[];
  allEquipment?: any[];
  allPorts?: any[];
}

/**
 * Helper to safely extract attributes object, handling string JSON if necessary.
 */
function getPduAttributes(pdu: any): Record<string, any> {
  let attrs = pdu?.attributes;
  if (typeof attrs === 'string') {
    try {
      attrs = JSON.parse(attrs);
    } catch {
      attrs = {};
    }
  }
  return attrs && typeof attrs === 'object' ? attrs : {};
}

/**
 * Natural sort chunks for alphanumeric names with multiple numbers (e.g. "Bank 1 - Outlet 2" vs "Bank 1 - Outlet 10").
 */
export function naturalSortChunks(name: string): (number | string)[] {
  const str = String(name || '');
  return str.split(/(\d+)/).map(chunk => {
    const n = parseInt(chunk, 10);
    return isNaN(n) ? chunk.toLowerCase() : n;
  });
}

/**
 * Full natural comparison for strings containing numeric chunks.
 */
export function compareNatural(a: string, b: string): number {
  const chunksA = naturalSortChunks(a);
  const chunksB = naturalSortChunks(b);
  const len = Math.max(chunksA.length, chunksB.length);
  for (let i = 0; i < len; i++) {
    const valA = chunksA[i];
    const valB = chunksB[i];
    if (valA === undefined) return -1;
    if (valB === undefined) return 1;
    if (typeof valA === 'number' && typeof valB === 'number') {
      if (valA !== valB) return valA - valB;
    } else {
      const cmp = String(valA).localeCompare(String(valB));
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
}

/**
 * Natural sort key for alphanumeric names.
 * Extracts the last number in the string (the outlet index, e.g. for "Bank 1 - Outlet 12" it returns 12).
 */
export function naturalSortKey(name: string): [number, string] {
  const str = String(name || '');
  const matches = str.match(/\d+/g);
  const num = matches && matches.length > 0 ? parseInt(matches[matches.length - 1], 10) : 999999;
  return [num, str];
}

/**
 * Resolves C13 vs C19 connector types, supporting extended standards (C15/C21) and generic 'other'.
 */
export function resolveConnectorType(port: any): SocketConnectorType {
  const text = `${port?.connector_type || ''} ${port?.port_type || ''} ${port?.name || ''}`.toLowerCase();
  if (text.includes('c19') || text.includes('c20') || text.includes('c21') || text.includes('c22')) return 'C19';
  if (text.includes('c13') || text.includes('c14') || text.includes('c15') || text.includes('c16')) return 'C13';
  if (port?.connector_type || (port?.port_type && !['power', 'pdu'].includes(String(port.port_type).toLowerCase()))) {
    return 'other';
  }
  return 'C13';
}

/**
 * Resolves feed letter (A vs B), parsing attributes if stored as string.
 */
export function resolveFeedLetter(pdu: any): 'A' | 'B' {
  const mount = String(pdu?.mounting_position || '').toLowerCase();
  const name = String(pdu?.name || '').toUpperCase();
  const attrs = getPduAttributes(pdu);
  const feedName = String(attrs.feed_name || attrs.feed || '').toUpperCase();

  if (mount.includes('right') || name.includes('PDU-B') || feedName.includes('B') || name.endsWith('-B') || name.endsWith(' B')) {
    return 'B';
  }
  return 'A';
}

/**
 * Resolves phase description.
 */
export function resolvePduPhase(pdu: any): string {
  const attrs = getPduAttributes(pdu);
  const raw = String(attrs.phase || '').toLowerCase();
  if (raw === 'phase_a' || raw === 'l1' || raw === 'a') return 'L1';
  if (raw === 'phase_b' || raw === 'l2' || raw === 'b') return 'L2';
  if (raw === 'phase_c' || raw === 'l3' || raw === 'c') return 'L3';
  if (raw.includes('3') || raw.includes('three')) return '3-Phase';
  return 'L1';
}

/**
 * Resolves rated current amperage (default 32A).
 */
export function resolveRatedCurrent(pdu: any): number {
  const attrs = getPduAttributes(pdu);
  const val = Number(attrs.rated_current_a);
  return Number.isFinite(val) && val > 0 ? val : 32;
}

/**
 * Builds structured PDU outlet matrix.
 */
export function buildPduOutletMatrix({
  pdu,
  pduPorts = [],
  cables = [],
  allEquipment = [],
  allPorts = [],
}: BuildPduOutletMatrixParams): PduOutletMatrixData {
  const pduId = String(pdu?.id || '');
  const pduName = String(pdu?.name || 'PDU');
  const feedLetter = resolveFeedLetter(pdu);
  const phase = resolvePduPhase(pdu);
  const ratedCurrentA = resolveRatedCurrent(pdu);

  // Ports resolution: from argument or pdu.ports
  const rawPorts = pduPorts && pduPorts.length > 0 ? pduPorts : (pdu?.ports || []);

  // Filter for power outlet ports (exclude console/mgmt if any)
  const NON_OUTLET_TYPES = ['ethernet', 'mgmt', 'console', 'serial', 'network', 'rj45'];
  const outletPorts = rawPorts.filter((p: any) => {
    const ptype = String(p.port_type || '').toLowerCase();
    return !NON_OUTLET_TYPES.includes(ptype);
  });

  // Full natural sort across multi-bank outlet names
  const sortedPorts = [...outletPorts].sort((a: any, b: any) => {
    return compareNatural(a.name, b.name);
  });

  // Build index of all ports and equipment
  const portIndex: Record<string, { equipment: any; port: any }> = {};

  // 1. From allEquipment (with nested ports)
  for (const eq of allEquipment) {
    if (Array.isArray(eq.ports)) {
      for (const p of eq.ports) {
        if (p?.id) {
          portIndex[String(p.id)] = { equipment: eq, port: p };
        }
      }
    }
  }

  // 2. From allPorts if supplied
  for (const p of allPorts) {
    if (p?.id && !portIndex[String(p.id)]) {
      const eq = allEquipment.find((e: any) => String(e.id) === String(p.equipment_id));
      if (eq) {
        portIndex[String(p.id)] = { equipment: eq, port: p };
      }
    }
  }

  // Build cable connection map strictly for this PDU's ports
  const pduPortIds = new Set(sortedPorts.map((p: any) => String(p.id)));
  const cableMap: Record<string, { cable: any; counterPortId: string }> = {};
  for (const c of cables) {
    const status = String(c.status || 'connected').toLowerCase();
    if (['decommissioned', 'inactive', 'disconnected', 'cut'].includes(status)) {
      continue;
    }
    const aId = String(c.port_a_id || '');
    const bId = String(c.port_b_id || '');

    if (pduPortIds.has(aId)) {
      cableMap[aId] = { cable: c, counterPortId: bId };
    } else if (pduPortIds.has(bId)) {
      cableMap[bId] = { cable: c, counterPortId: aId };
    }
  }

  // Construct socket items
  const sockets: PduSocketItem[] = sortedPorts.map((port: any, idx: number) => {
    const portId = String(port.id);
    const connectorType = resolveConnectorType(port);
    const [num] = naturalSortKey(port.name);
    const outletIndex = num !== 999999 ? num : idx + 1;

    const connectionInfo = cableMap[portId];
    if (connectionInfo) {
      const counterEntry = portIndex[connectionInfo.counterPortId];
      const counterEq = counterEntry?.equipment;
      const counterPort = counterEntry?.port;

      const deviceName = counterEq?.name || (
        connectionInfo.cable.port_a_id === connectionInfo.counterPortId
          ? connectionInfo.cable.device_a_name
          : connectionInfo.cable.device_b_name
      );
      const portName = counterPort?.name || (
        connectionInfo.cable.port_a_id === connectionInfo.counterPortId
          ? connectionInfo.cable.port_a_name
          : connectionInfo.cable.port_b_name
      );

      return {
        portId,
        outletIndex,
        name: port.name || `Outlet ${outletIndex}`,
        connectorType,
        status: 'occupied' as const,
        cableId: connectionInfo.cable.id,
        connectedDeviceId: counterEq?.id ? String(counterEq.id) : undefined,
        connectedDeviceName: deviceName,
        connectedDeviceU: counterEq?.u_position,
        connectedDeviceMount: counterEq?.mounting_position,
        connectedPortName: portName,
        powerDrawW: counterEq?.power_w,
      };
    }

    const portStatus = String(port.status || '').toLowerCase();
    const isReserved = portStatus === 'reserved';

    return {
      portId,
      outletIndex,
      name: port.name || `Outlet ${outletIndex}`,
      connectorType,
      status: isReserved ? ('reserved' as const) : ('free' as const),
    };
  });

  const totalOutlets = sockets.length;
  const occupiedOutlets = sockets.filter(s => s.status === 'occupied').length;
  const freeOutlets = sockets.filter(s => s.status === 'free').length;

  return {
    pduId,
    pduName,
    feedLetter,
    phase,
    ratedCurrentA,
    totalOutlets,
    freeOutlets,
    occupiedOutlets,
    sockets,
  };
}
